import { Map as ImmutableMap } from 'immutable';
import { createSelector } from 'reselect';

import { getLogger } from '../../utils/core';
import store from '../../utils/redux-store';
import { accountsSelectors, accountsUtils } from '../accounts';
import { selectorsHelpers } from '../core';
import { featureFlagsSelectors } from '../feature-flags';
import { preferencesSelectors } from '../preferences';
import { transactionsSelectors } from '../transactions';

import { AccountBalances, ShowBalance } from './types';

const logger = getLogger('balanceSelectors.js');

const { getTransactionsByAccountIdRaw, getTransactionsForAccountIdSelectors, TxnTypes } = transactionsSelectors;

function balRound(val) {
  const ret = val ? Number(val.toFixed(2)) : 0;
  return ret || 0;
}

//    Goal Balance Cache
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

// manual cache function so that we store only the last entry for each account
const goalCache = {};
function sumGoalBalance(accountId, accountTxns) {
  const storedValue = goalCache[accountId];
  if (storedValue && storedValue.balance !== undefined && storedValue.txnsHash === accountTxns.hashCode()) {
    return storedValue.balance;
  }
  logger.debug('Recalculating goal balance for accountId: ', accountId, '\nhash', accountTxns.hashCode());

  const balance = balRound(accountTxns.reduce((sum, txn) => sum - Number(txn.amount), 0));

  goalCache[accountId] = {
    txnsHash: accountTxns.hashCode(),
    balance,
  };
  return balance;
}

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
//                                Standard Selectors
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

// expects props: (state, accountId)
export const getBalanceForAccountId = createSelector(
  accountsSelectors.getAccountById,
  preferencesSelectors.getShowBalance,
  (state) => featureFlagsSelectors.getFeatureFlag(state, 'balanceToggle'),
  (account, balanceType, balanceToggleEnabled) => {
    if (account && !account.isClosed) {
      if (account.type === 'INVESTMENT') {
        if (accountsUtils.isAccountManual(account)) {
          return account.currentBalanceAsOf || 0;
        }
        return account.onlineBalance || 0;
      }

      if (accountsUtils.isAccountManual(account)) {
        return account.currentBalanceAsOf || 0;
      }

      if (accountsUtils.isLoanAccount(account) || (balanceToggleEnabled && balanceType === ShowBalance.BANK_REPORTED)) {
        return account.onlineBalance || 0;
      }

      return (account.currentBalanceAsOf ?? account.onlineBalance) || 0;
    }
    return 0;
  },
);

const onlyGoalProps = { txnTypes: TxnTypes.GOALS };

// expects props: (state, accountId)
export const getGoalBalanceForAccountId = createSelector(
  getTransactionsByAccountIdRaw,
  (state) => getTransactionsForAccountIdSelectors(state, onlyGoalProps), // filter to goal txns
  (state, accountId) => accountId,
  (txnsByAccountId, txnsForAccountIdSelectors, accountId) => {
    if (accountId) {
      const txnsForAccountIdSelector = txnsForAccountIdSelectors.get(accountId); // grab the goal-filtered txn selector
      if (!txnsForAccountIdSelector) {
        assert(false, 'transaction selector missing, tell Chris to investigate');
        return 0;
      }
      const accountGoalTxns = txnsForAccountIdSelector(txnsByAccountId);
      return sumGoalBalance(accountId, accountGoalTxns);
    }
    return 0;
  },
);

let balanceCache = ImmutableMap();

// Selector for accountsList / accountNodes
/** Only use if you need ALL balances for display! If you only need one balance, use balanceForAccountId */
// expects props: (state)
export const getBalancesByAccountIds = createSelector(
  selectorsHelpers.createTrimSelector(accountsSelectors.getAccountsById, (account) => ({
    id: account.id,
    name: account.name,
    type: account.type,
    onlineBalance: account.onlineBalance,
    onlineBalanceAt: account.onlineBalanceAt?.slice?.(0, 10),
    isClosed: account.isClosed,
    currentBalanceAsOf: account.currentBalanceAsOf,
  })),
  (state) => featureFlagsSelectors.getFeatureFlag(state, 'investmentAccountGoalContributions'),
  // the next props are here for subscription recaclc
  preferencesSelectors.getShowBalance,
  (state) => featureFlagsSelectors.getFeatureFlag(state, 'balanceToggle'),
  getTransactionsByAccountIdRaw,
  (accountsById, investmentAccountGoalContributions, ..._others) => {
    const state = store.getState();
    let cacheBreak = false;

    const balancesByAccountId = accountsById.reduce((idMap, account) => {
      const balance = getBalanceForAccountId(state, account.id);
      const goalsBalance =
        account.type !== 'INVESTMENT' || investmentAccountGoalContributions
          ? getGoalBalanceForAccountId(state, account.id)
          : 0;
      const cachedValue = balanceCache.get(account.id);
      if (cacheBreak) {
        // cache already broken
      } else if (!cachedValue || cachedValue.balance !== balance || cachedValue.goalsBalance !== goalsBalance) {
        logger.debug('Cache broke for account: ', account.name);
        cacheBreak = true;
      }
      const availableBalance = balRound(balance - goalsBalance) || 0;
      return idMap.set(
        account.id,
        AccountBalances({
          accountId: account.id,
          accountType: account.type,
          balance,
          goalsBalance,
          availableBalance,
        }),
      );
    }, ImmutableMap());

    if (cacheBreak === false) {
      logger.debug('NO RECALC - using cached balances');
    }
    balanceCache = cacheBreak ? balancesByAccountId : balanceCache;
    return balanceCache;
  },
);

// These should no longer be necessary as reports now is relying on accountsBalances endpoint instead of holdings balances

//  Investment Balance selectors
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

// export const getInvestmentBalancesByAccountId = createSelector(
//   selectorsHelpers.createTrimSelector(accountsSelectors.getInvestmentAccounts, (investmentAccount) => ({
//     id: investmentAccount.id,
//     type: investmentAccount.type,
//     onlineBalance: investmentAccount.onlineBalance,
//     currentBalanceAsOf: investmentAccount.currentBalanceAsOf,
//   })),
//   investmentHoldingsV2Selectors.getInvestmentHoldingsBalancesByAccountId,
//   (investmentAccounts, holdingsBalancesByAccountId) => investmentAccounts.map((account) => AccountBalances({
//     accountId: account.id,
//     accountType: account.type,
//     balance: account.onlineBalance ?? holdingsBalancesByAccountId.get(account.id),
//     availableBalance: account.onlineBalance ?? holdingsBalancesByAccountId.get(account.id),
//   })),
// );
